import { useCallback, useEffect, useState } from "react";
import useEmblaCarousel from "embla-carousel-react";
import { ArrowLeft, ArrowRight, Quote } from "lucide-react";
import { REVIEWS } from "./data";
import SectionHeader from "./SectionHeader";

export default function Reviews() {
  const [emblaRef, emblaApi] = useEmblaCarousel({
    loop: true,
    align: "start",
    slidesToScroll: 1,
    dragFree: false,
  });
  const [index, setIndex] = useState(0);

  const scrollPrev = useCallback(() => emblaApi && emblaApi.scrollPrev(), [emblaApi]);
  const scrollNext = useCallback(() => emblaApi && emblaApi.scrollNext(), [emblaApi]);

  useEffect(() => {
    if (!emblaApi) return;
    const on = () => setIndex(emblaApi.selectedScrollSnap());
    emblaApi.on("select", on);
    return () => emblaApi.off("select", on);
  }, [emblaApi]);

  return (
    <section
      id="reviews"
      data-testid="reviews-section"
      className="relative bg-[color:var(--ink)] text-[color:var(--paper)]"
    >
      <div className="mx-auto max-w-[1440px] px-5 sm:px-8 lg:px-12 py-16 sm:py-20 lg:py-24">
        <SectionHeader
          eyebrow="Section 05 · Guest Reviews & Voices"
          title={
            <>
              What guests <span className="font-display-italic text-[color:var(--sand)]">say</span> after they leave.
            </>
          }
          intro="Ten guests, ten quiet weekends, one estate."
          invert
        />

        <div className="mt-12 sm:mt-16 relative">
          <div ref={emblaRef} className="overflow-hidden">
            <div className="flex gap-6 lg:gap-8">
              {REVIEWS.map((r, i) => (
                <article
                  key={i}
                  data-testid={`review-card-${i}`}
                  className="flex-[0_0_88%] sm:flex-[0_0_60%] lg:flex-[0_0_42%] xl:flex-[0_0_36%] rounded-[24px] border hairline-inv p-7 sm:p-9 bg-[color:var(--ink)]"
                >
                  <Quote size={26} className="text-[color:var(--sand)]" />
                  <p className="mt-5 font-display text-[1.2rem] sm:text-[1.35rem] leading-[1.45]">
                    “{r.body}”
                  </p>
                  <div className="mt-7 flex items-center justify-between text-[color:var(--paper)]/75">
                    <div className="font-mono-alt">{r.name}</div>
                    <div className="font-mono-alt text-[color:var(--paper)]/50">{r.from}</div>
                  </div>
                </article>
              ))}
            </div>
          </div>

          <div className="mt-8 flex items-center justify-between">
            <div className="font-mono-alt text-[color:var(--paper)]/60">
              {String(index + 1).padStart(2, "0")}
              <span className="text-[color:var(--paper)]/30 mx-2">/</span>
              {String(REVIEWS.length).padStart(2, "0")}
            </div>
            <div className="flex items-center gap-3">
              <button
                data-testid="reviews-prev"
                onClick={scrollPrev}
                aria-label="Previous review"
                className="h-11 w-11 rounded-full border hairline-inv flex items-center justify-center hover:bg-[color:var(--paper)] hover:text-[color:var(--ink)] transition-colors"
              >
                <ArrowLeft size={18} />
              </button>
              <button
                data-testid="reviews-next"
                onClick={scrollNext}
                aria-label="Next review"
                className="h-11 w-11 rounded-full border hairline-inv flex items-center justify-center hover:bg-[color:var(--paper)] hover:text-[color:var(--ink)] transition-colors"
              >
                <ArrowRight size={18} />
              </button>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
